import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './SeatSelector.css';

// 🚌 Sélecteur de sièges style opérateur (FlixBus) :
//  - En-tête : volant centré (poste de conduite), flèches de niveau si bus à étage.
//  - Grille numérotée 2+2 (ou plan réel), couloir central. Chaque siège = ICÔNE :
//      indispo = grisé + croix · dispo = couleur de SA CLASSE tarifaire.
//  - Emplacements spéciaux (WC 🚻, escalier 🪜) intégrés dans la grille à leur vraie rangée
//    (l'escalier bascule vers le niveau 2). PAS de « sortie de secours » (non standard).
//  - Légende des classes + symbole indisponible en bas.
//
// Props :
//   plan     : { decks:[{level,cols,aisleAfter,special,rows}], classes:{siege→classId} } (v2)
//   classes  : [{ id, nom, couleur, prix }] catalogue de la compagnie
//   seats    : [{ number, available, blocked, classId, prix }]
//   selectedSeat, onSeatSelect, loading, capacity
const DEFAULT_COLOR = '#16a34a';

const SeatSelector = ({ plan = null, classes = [], seats = [], selectedSeat, onSeatSelect, loading = false, capacity = 0 }) => {
  const { t } = useTranslation('payment');
  const K = (k, o) => t('seat.' + k, o);
  const [deckIdx, setDeckIdx] = useState(0);

  const byNumber = useMemo(() => new Map(seats.map(s => [s.number, s])), [seats]);
  const classById = useMemo(() => new Map((classes || []).map(c => [c.id, c])), [classes]);

  // Repli : pas de plan v2 fourni → construire un plan 2+2 à partir de la capacité.
  const decks = useMemo(() => {
    if (plan && Array.isArray(plan.decks) && plan.decks.length) return plan.decks;
    const cap = Math.max(0, parseInt(capacity, 10) || seats.length || 0);
    const rows = [];
    for (let n = 1; n <= cap; n += 4) {
      const row = [];
      for (let c = 0; c < 4; c++) row.push(n + c <= cap ? { kind: 'seat', number: n + c } : null);
      rows.push(row);
    }
    return [{ level: 1, cols: 4, aisleAfter: 2, special: [], rows }];
  }, [plan, capacity, seats.length]);

  const deck = decks[Math.min(deckIdx, decks.length - 1)] || decks[0];
  const aisleAfter = deck?.aisleAfter ?? 2;

  const priceFmt = (p) => Number(p || 0).toLocaleString('fr-FR');

  if (loading) return <div className="ss-wrapper"><div className="ss-loading">{K('loading', 'Chargement du plan…')}</div></div>;
  if (!deck || seats.length === 0) return <div className="ss-wrapper"><div className="ss-empty">{K('none', 'Aucun siège disponible')}</div></div>;

  const clickSeat = (num, available) => {
    if (!available) return;
    onSeatSelect(num === selectedSeat ? null : num);
  };

  // Rendu d'une cellule (siège / spécial / vide).
  const renderCell = (cell, key) => {
    if (cell == null) return <span key={key} className="ss-cell ss-cell--empty" aria-hidden="true" />;
    if (cell.kind === 'special') {
      const isStairs = cell.type === 'stairs';
      return (
        <button
          key={key}
          type="button"
          className={`ss-cell ss-special ss-special--${cell.type}`}
          title={isStairs ? K('upperDeck', 'Étage supérieur') : K('wc', 'Toilettes')}
          onClick={isStairs && decks.length > 1 ? () => setDeckIdx(deckIdx === 0 ? 1 : 0) : undefined}
          disabled={!isStairs || decks.length <= 1}
        >
          {isStairs ? '🪜' : '🚻'}
        </button>
      );
    }
    // Siège
    const info = byNumber.get(cell.number) || { number: cell.number, available: true };
    const unavailable = cell.blocked || !info.available;
    const isSel = cell.number === selectedSeat;
    const cls = info.classId ? classById.get(info.classId) : null;
    const color = cls?.couleur || DEFAULT_COLOR;
    const style = unavailable ? undefined
      : isSel ? { background: color, borderColor: color, color: '#fff' }
      : { borderColor: color, color, background: color + '18' };
    return (
      <button
        key={key}
        type="button"
        className={`ss-cell ss-seat${unavailable ? ' ss-seat--off' : ''}${isSel ? ' ss-seat--sel' : ''}`}
        style={style}
        disabled={unavailable}
        onClick={() => clickSeat(cell.number, !unavailable)}
        title={unavailable ? `${K('seatWord', 'Siège')} ${cell.number} — ${K('stOccupied', 'indisponible')}`
          : `${K('seatWord', 'Siège')} ${cell.number}${cls ? ` · ${cls.nom} · ${priceFmt(info.prix)} FCFA` : ''}`}
        aria-label={`${K('seatWord', 'Siège')} ${cell.number}`}
      >
        {unavailable ? '✕' : cell.number}
      </button>
    );
  };

  // Numéro de rangée : compteur qui n'incrémente que sur les rangées CONTENANT un siège
  // (les rangées uniquement spéciales créent un saut de numérotation, comme chez les opérateurs).
  let rowNo = 0;

  return (
    <div className="ss-wrapper">
      {/* En-tête : volant centré + flèches de niveau (si étage) */}
      <div className="ss-head">
        {/* Volant DESSINÉ (SVG) — pas un emoji. 🎡 (grande roue) était un mauvais glyphe. */}
        <svg className="ss-wheel" viewBox="0 0 24 24" role="img" aria-label={K('driver', 'Poste de conduite')}>
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="12" cy="12" r="2.4" fill="currentColor" />
          <line x1="12" y1="3.2" x2="12" y2="9.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="4.4" y1="16.6" x2="9.9" y2="13.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="19.6" y1="16.6" x2="14.1" y2="13.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        {decks.length > 1 && (
          <div className="ss-deck-nav">
            <button type="button" className="ss-deck-arrow" onClick={() => setDeckIdx(i => Math.max(0, i - 1))} disabled={deckIdx === 0} aria-label="Niveau précédent">‹</button>
            <span className="ss-deck-label">{K('level', 'Niveau')} {deck.level}</span>
            <button type="button" className="ss-deck-arrow" onClick={() => setDeckIdx(i => Math.min(decks.length - 1, i + 1))} disabled={deckIdx >= decks.length - 1} aria-label="Niveau suivant">›</button>
          </div>
        )}
      </div>

      {/* Grille */}
      <div className="ss-grid">
        {deck.rows.map((row, ri) => {
          const hasSeat = row.some(c => c && c.kind === 'seat');
          const label = hasSeat ? String(++rowNo) : '';
          return (
            <div key={ri} className="ss-row">
              <span className="ss-rownum">{label}</span>
              <div className="ss-row-cells">
                {row.map((cell, ci) => (
                  <React.Fragment key={ci}>
                    {ci === aisleAfter && <span className="ss-aisle" />}
                    {renderCell(cell, `${ri}-${ci}`)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Légende : classes (couleur + prix) + indisponible */}
      <div className="ss-legend">
        {(classes || []).map(c => (
          <span key={c.id} className="ss-leg">
            <span className="ss-leg-dot" style={{ background: c.couleur, borderColor: c.couleur }} />
            {c.nom} · {priceFmt(c.prix)} FCFA
          </span>
        ))}
        {/* Aucune classe configurée → on explique la couleur par défaut (« Disponible »)
            au lieu de laisser des sièges verts sans libellé. Les couleurs par classe
            apparaissent dès que la compagnie crée + assigne ses classes (écran Classes & tarifs). */}
        {(!classes || classes.length === 0) && (
          <span className="ss-leg">
            <span className="ss-leg-dot" style={{ background: DEFAULT_COLOR, borderColor: DEFAULT_COLOR }} />
            {K('available', 'Disponible')}
          </span>
        )}
        <span className="ss-leg">
          <span className="ss-leg-dot ss-leg-dot--off">✕</span>
          {K('occupied', 'Indisponible')}
        </span>
      </div>

      {selectedSeat && (
        <div className="ss-selected">
          {K('selectedPre', 'Siège')} <strong>{selectedSeat}</strong>
          {(() => { const s = byNumber.get(selectedSeat); const c = s?.classId ? classById.get(s.classId) : null;
            return c ? <> · {c.nom} · <strong>{priceFmt(s.prix)} FCFA</strong></> : null; })()}
        </div>
      )}
    </div>
  );
};

export default SeatSelector;
